import { Alert, Button, Card, Col, Container, ListGroup, ListGroupItem, Row } from "react-bootstrap";
import { AppAuthStateProps } from '../../types/propTypes';

import './Trackboard.css';

import { TrackboardItem } from './TrackboardItem';

import { faChevronDown, faChevronUp, faEye, faWifi } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import pLimit from "p-limit";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FhirPatientVisit, callFhirPatient } from "../../amplify/api-gateway/fetch-sync-fhir";
import { ACTIVE_PATIENT_VISITS_WITH_METADATA, CDU_PATIENT_VISITS_WITH_METADATA, CURRENT_DELAY, Diagnosis, FAVORITES_FOR_USERNAME_AND_ACTIVE_PATIENTS, FAVORITE_LISTS_FOR_USERNAME, Favorite, Note, PAST_FAVORITES_FOR_ACTIVE_PATIENTS, RELEVANT_COMPASS_DIAGNOSES_FOR_ACTIVE_PATIENTS, callQuery } from "../../amplify/api-gateway/query";
import { PatientVisit, PatientVisitMetadata } from '../../amplify/api-gateway/search-patients';
import { isDemoUser } from "../../utils/demo";
import { getStorage } from "../../utils/storage";
import { getRelativeDate } from "../../utils/time";
import { useGlobalOptions } from "../common/GlobalOptionsContext";
import { DISPOSITION_MAPPING } from "../ops/metricsPlotOpts";
import { FavoriteColumnSelectModal } from "./FavoriteColumnSelectModal";
import { FavoriteContextMenu } from "./FavoriteContextMenu";
import { FavoriteNewListModal } from "./FavoriteNewListModal";
import { formatCsnToFavorites, getDefaultAugmentedPatient, getDefaultColumns, getDemoMrnToName, sortPatientsByCol, sortPatientsDefault, updatePatientWithMrnToName } from "./trackboardUtils";


const FILTER_OPTIONS = ["All", "COMPASS", "Waiting", "Alpha", "Bravo", "Cardinal", "Delta", "Emerald", "Foxtrot", "Indigo", "Jade"]

export const Trackboard = (props: AppAuthStateProps) => {

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getDerivedRole, getTrackboardColumns } = useGlobalOptions();
    const filter = searchParams.get('filter') || 'All';

    const [isLoading, setIsLoading] = useState(false)
    const [sortColId, setSortColId] = useState<string>();
    const [sortColAsc, setSortColAsc] = useState<boolean>(true);
    const [activeFilter, setActiveFilter] = useState(filter);
    const [allPatients, setAllPatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata)[]>([]);
    const [activePatients, setActivePatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata)[]>([]);
    const [mrnToName, setMrnToName] = useState<any>({});
    const [mrnToDiagnosisStr, setMrnToDiagnosisStr] = useState<any>({});
    const [csnToFavorites, setCsnToFavorites] = useState<any>({});
    const [csnToReviewed, setCsnToReviewed] = useState<any>({});
    const [mrnToPastFavorites, setMrnToPastFavorites] = useState<any>({});
    const [mrnToPastReviewed, setMrnToPastReviewed] = useState<any>({});
    const [lastUpdatedTime, setLastUpdatedTime] = useState<any>(null);
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [contextMenu, setContextMenu] = useState<any>({x: 0, y: 0, csn: null});
    const [showNewList, setShowNewList] = useState<boolean>(false);
    const [showColumnSelect, setShowColumnSelect] = useState<boolean>(false);
    const [favoriteLists, setFavoriteLists] = useState<string[]>([]);
    const [cduPatients, setCDUPatients] = useState<string[]>([]);
    const [columns, setColumns] = useState<any[]>([]);

    useEffect(() => {
        const colsFromSettings = getTrackboardColumns();
        const role = getDerivedRole(props.user);
        setColumns(getDefaultColumns(role, colsFromSettings));
    }, [getTrackboardColumns, getDerivedRole, props.user]);

    const filterPatientsByBed = (bed: string, patients?: (PatientVisit & FhirPatientVisit & PatientVisitMetadata)[]) => {
        const pts = patients ? patients : allPatients;

        if (bed === "COMPASS") {
            return pts.filter((p) => {
                let isAdmit = false;
                if (p.ed_dispo && DISPOSITION_MAPPING[p.ed_dispo] === "Admit") {
                    isAdmit = true;
                }
                let isEnglish = !p.languages || p.languages.length === 0 || (p.languages?.length > 0 && p.languages[0] === "English");
                const isRiskHigh = p.epic_revisit_score && p.epic_revisit_score.length > 0 &&  Number(p.epic_revisit_score[0]) >= 0.52;

                return !isAdmit && isEnglish && isRiskHigh;
            });
        } else if (bed === "Alpha") {
            return pts.filter((p) => (p.bed?.startsWith("A") && !p.bed?.startsWith("ADULT")));
        } else if (bed === "Bravo") {
            return pts.filter((p) => p.bed?.startsWith("B"));
        } else if (bed === "Cardinal") {
            return pts.filter((p) => p.bed?.startsWith("C"));
        } else if (bed === "Delta") {
            return pts.filter((p) => p.bed?.startsWith("D"));
        } else if (bed === "Waiting") {
            return pts.filter((p) => (p.bed?.startsWith("EDW") || (p.bed?.startsWith("Waiting"))));
        } else if (bed === "Emerald") {
            return pts.filter((p) => (p.bed?.startsWith("E") && !p.bed?.startsWith("EDW")));
        } else if (bed === "Foxtrot") {
            return pts.filter((p) => p.bed?.startsWith("F"));
        } else if (bed === "Indigo") {
            return pts.filter((p) => p.bed?.startsWith("I"));
        } else if (bed === "Jade") {
            return pts.filter((p) => p.bed?.startsWith("J"));
        } else {
            return pts;
        }
    }

    const sortPatients = (col: string) => {
        setActivePatients((activePts: any) => {
            const pts = [...activePts];
            let asc = sortColAsc;
            if (sortColId === col) {
                if (sortColAsc === false) {
                    // Reset to original state
                    col = "bed";
                    setSortColId("bed");
                    setSortColAsc(true);
                    asc = true;
                } else {
                    asc = false;
                    setSortColAsc(asc);
                }
            } else {
                setSortColId(col);
                setSortColAsc(true);
            }
    
            return sortPatientsByCol(pts, col, asc, props.tenant);
        });
    }

    const loadPatientNames = async (patients: PatientVisit[]) => {
        const limit = pLimit(2);
        const localMrnToName: any = {
            ...mrnToName,
        };

        const promises: any[] = [];
        for (let patient of patients) {
            const mrn = patient.mrn;
            if (!(mrn in localMrnToName)) {
                promises.push(limit(() => {
                    return new Promise((resolve: any, reject: any) => {
                        callFhirPatient({
                            mrn: patient.mrn,
                        }).then((pt: FhirPatientVisit) => {
                            localMrnToName[patient.mrn] = pt;
                            // Update the names slowly, to avoid hogging the bandwidth
                            // which will prevent users from clicking on the patients
                            if (Object.keys(localMrnToName).length % 10 === 0) {
                                setMrnToName({
                                    ...localMrnToName
                                });
                            }
                            resolve(pt)
                        }).catch((err: any) => {
                            reject({});
                        })
                    })
                }))
            }
        }

        const result = await Promise.all(promises);
        for (let fhirPatientInfo of result) {
            localMrnToName[fhirPatientInfo.mrn] = fhirPatientInfo;
        }
        setMrnToName(localMrnToName);
    }
    
    const refreshBaseData = () => {
        setIsLoading(true);
        callQuery({
            operation: ACTIVE_PATIENT_VISITS_WITH_METADATA,
        })
        .then((patients: PatientVisit[]) => {
            sortPatientsDefault(patients);

            if (!isDemoUser(props.user, props.tenant)) {
                loadPatientNames(patients).then(() => {});
            } else {
                setMrnToName(getDemoMrnToName(patients));
            }
            setIsLoading(false);

            const augmentedPatients = patients.map((p: PatientVisit) => {
                return getDefaultAugmentedPatient(p);
            });
            setAllPatients(augmentedPatients);

            setActivePatients([
                ...filterPatientsByBed(activeFilter, augmentedPatients),
            ]);
        });

        callQuery({
            operation: RELEVANT_COMPASS_DIAGNOSES_FOR_ACTIVE_PATIENTS,
        })
        .then((relevantDiagnoses: Diagnosis[]) => {
            const mrnToDiagnoses: any = {};
            for (let diagnosis of relevantDiagnoses) {
                if (!(diagnosis.mrn in mrnToDiagnoses)) {
                    mrnToDiagnoses[diagnosis.mrn] = new Set();
                }
                mrnToDiagnoses[diagnosis.mrn].add(diagnosis.desc10);
            }
            const mrnToDiagnosisStr: any = {};
            for (let mrn of Object.keys(mrnToDiagnoses)) {
                mrnToDiagnosisStr[mrn] = Array.from(mrnToDiagnoses[mrn]).join(",");
            }
            setMrnToDiagnosisStr(mrnToDiagnosisStr);
        });

        callQuery({
            operation: CDU_PATIENT_VISITS_WITH_METADATA,
        })
        .then((patients: any[]) => {
            setCDUPatients(patients);
        });

        callQuery({
            operation: CURRENT_DELAY,
        })
        .then((delay: number | null) => {
            if (delay) {
                setLastUpdatedTime(new Date(Date.now() - delay * 1000));
            } else {
                setLastUpdatedTime(null);
            }
        });

        callQuery({
            operation: FAVORITES_FOR_USERNAME_AND_ACTIVE_PATIENTS,
        })
        .then((scores: any) => {
            const mapFavorites: any = {};
            const mapReviewed: any = {};
            for (let score of scores) {
                const csn = score["csn"];
                if (score["favorite_type"] === "reviewed") {
                    mapReviewed[csn] = score;
                } else if (score["favorite_type"] !== "recent") {
                    if (!(csn in mapFavorites)) {
                        mapFavorites[csn] = [];
                    }
                    mapFavorites[csn].push(score);
                } 
            }
            setCsnToFavorites(mapFavorites);
            setCsnToReviewed(mapReviewed);
        });

        callQuery({
            operation: PAST_FAVORITES_FOR_ACTIVE_PATIENTS,
        })
        .then((favorites: any) => {
            const mapFavorites: any = {};
            const mapReviewed: any = {};
            for (let favorite of favorites) {
                const mrn = favorite["mrn"];
                if (favorite["favorite_type"] === "reviewed") {
                    mapReviewed[mrn] = favorite;
                } else if (favorite["favorite_type"] !== "recent") {
                    if (!(mrn in mapFavorites)) {
                        mapFavorites[mrn] = [];
                    }
                    mapFavorites[mrn].push(favorite);
                }
            }
            setMrnToPastFavorites(mapFavorites);
            setMrnToPastReviewed(mapReviewed);
        });

        callQuery({
            operation: FAVORITE_LISTS_FOR_USERNAME,
        })
        .then((lists: any) => {
            lists = [
                "favorite",
                ...lists.map((list: any) => list.list_name)
            ];
            setFavoriteLists(lists);
        });
    }

    useEffect(() => {
        refreshBaseData();
    }, []);

    useEffect(() => {
        setActivePatients([
            ...filterPatientsByBed(activeFilter),
        ])
    }, [activeFilter]);
    
    useEffect(() => {
        setAllPatients((pts: any) => {
            return [
                ...pts.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata)) => {
                    return updatePatientWithMrnToName(patient, mrnToName);
                })),
            ];
        });
        setActivePatients((pts: any) => {
            return [
                ...filterPatientsByBed(activeFilter, pts).map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata)) => {
                    return updatePatientWithMrnToName(patient, mrnToName);
                })),
            ]
        })
    }, [mrnToName]);

    const handleFilterChange = (newFilter: string) => {
        setSearchParams({ filter: newFilter });

        // Set the filter state
        setActiveFilter(newFilter);
    };

    const handleClear = () => {
        setShowContextMenu(false);
    };

    const updateCsnToFavorites = (csn: string, favoriteType: string, favorite?: Favorite) => {
        console.log("updateCsnToFavorites", favoriteType)
        const map: any = {...csnToFavorites};
        formatCsnToFavorites(map, csn, favoriteType, favorite);
        setCsnToFavorites(map);
    };

    const updatePatientWithNote = (note: Note) => {
        setActivePatients(activePatients =>
            activePatients.map(pt => 
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
    };

    return (
        <>
            <Container style={{ paddingTop: isDemoUser(props.user) ? '143px' : '64px', paddingLeft: '32px', paddingRight: '32px' }} onClick={handleClear} fluid>

                <Row style={{marginBottom: '12px'}}>
                    <Col md={9}>
                        {
                            FILTER_OPTIONS.map(opt => {
                                if (activeFilter === opt) {
                                    return (
                                        <Button key={opt} size="sm" variant="primary" className="trackboard-quick-actions-btn" onClick={() => {handleFilterChange(opt)}}>{opt} ({filterPatientsByBed(opt).length})</Button>
                                    )
                                } else {
                                    return (
                                        <Button key={opt} size="sm" variant="outline-secondary" className="trackboard-quick-actions-btn" onClick={() => {handleFilterChange(opt)}}>{opt} ({filterPatientsByBed(opt).length})</Button>
                                    )
                                }
                            })
                        }
                        <Button size="sm" variant={"outline-secondary"} className="trackboard-quick-actions-btn" onClick={() => { navigate("/cdu"); }}>CDU ({cduPatients.length})</Button>
                    </Col>
                    <Col md={3} className="text-right trackboard-monitor-updated">
                        <small>
                            {
                                lastUpdatedTime && (
                                    <>
                                        <FontAwesomeIcon icon={faWifi} />{' '}monitor updated {getRelativeDate(lastUpdatedTime)}
                                    </>
                                )
                            }
                        </small>
                    </Col>
                </Row>

                {
                    getDerivedRole(props.user) === "ADMIN" && (
                        <Row>
                            <Col>
                                <Alert variant="secondary"><strong>Beta Feature</strong> Try the new trackboard with explanations <a href="#" onClick={() => window.open(`/trackboard-v2`, "_blank")}>here</a></Alert>
                            </Col>
                        </Row>
                    )
                }
                <Row style={{marginBottom: '24px'}}>
                    <Col>
                        <Card className="trackboard-table">
                            <ListGroup variant="flush">
                                <ListGroupItem className="d-flex flex-column trackboard-table-header">
                                    <div style={{display: 'block', width: '100%'}} >
                                        {
                                            columns.map((col) => {
                                                const totalRelativeCols = columns.reduce((acc, obj) => acc + (obj.size ?? 0), 0);
                                                if (col.id === "action") {
                                                    return (
                                                        <div key={col.id} className="text-right" style={{display: 'inline-block', width: `${100 * col.size/totalRelativeCols}%`}}>
                                                            <Button
                                                                variant="primary"
                                                                className={"trackboard-show-btn"}
                                                                onClick={() => setShowColumnSelect(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </Button>
                                                        </div>
                                                    )
                                                } else {
                                                    return (
                                                        <div key={col.id} style={{display: 'inline-block', cursor: 'pointer', width: `${100 * col.size/totalRelativeCols}%`}} onClick={() => sortPatients(col.id)}>{col.name}{sortColId === col.id ? sortColAsc ? <>{' '}<FontAwesomeIcon icon={faChevronDown} /></> : <>{' '}<FontAwesomeIcon icon={faChevronUp} /></> : <></>}</div>
                                                    )
                                                }
                                            })
                                        }
                                    </div>
                                </ListGroupItem>
                                {
                                    isLoading ? (
                                        <>
                                            {
                                                [1, 2, 3].map((v) => (
                                                    <TrackboardItem 
                                                        key={v}
                                                        /** @ts-ignore */
                                                        patient={null}
                                                        columns={columns} 
                                                        csnToFavorites={csnToFavorites}
                                                        csnToReviewed={csnToReviewed}
                                                        pastFavorites={[]}
                                                        pastReviewed={[]}
                                                        setCsnToReviewed={setCsnToReviewed}
                                                        setShowContextMenu={setShowContextMenu}
                                                        setContextMenu={setContextMenu}
                                                        padNotes={true}
                                                        isPlaceholder={true}
                                                        updatePatientWithNote={updatePatientWithNote}
                                                        tenant={props.tenant}
                                                        user={props.user}
                                                    />
                                                ))
                                            }
                                        </>
                                    ) : (
                                        <>{
                                            activePatients.map((pt) => (
                                                <TrackboardItem 
                                                    key={pt.csn}
                                                    patient={pt}
                                                    columns={columns}
                                                    csnToFavorites={csnToFavorites}
                                                    csnToReviewed={csnToReviewed}
                                                    pastFavorites={mrnToPastFavorites[pt.mrn]}
                                                    pastReviewed={mrnToPastReviewed[pt.mrn]}
                                                    setCsnToReviewed={setCsnToReviewed}
                                                    padNotes={true}
                                                    setShowContextMenu={setShowContextMenu}
                                                    setContextMenu={setContextMenu}
                                                    updatePatientWithNote={updatePatientWithNote}
                                                    mrnToRelevantPastDiagnosesForCompass={mrnToDiagnosisStr}
                                                    tenant={props.tenant}
                                                    user={props.user}
                                                />
                                            ))
                                        }</>
                                    )
                                }
                            </ListGroup>
                        </Card>
                    </Col>
                </Row>
                <FavoriteContextMenu show={showContextMenu} contextMenu={contextMenu} lists={favoriteLists} setShow={setShowContextMenu} setShowNewList={setShowNewList} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteNewListModal show={showNewList} setShow={setShowNewList} lists={favoriteLists} setLists={setFavoriteLists} contextMenu={contextMenu} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteColumnSelectModal show={showColumnSelect} setShow={setShowColumnSelect} columns={columns} setColumns={setColumns} allColumns={getDefaultColumns(getDerivedRole(props.user))} saveKey="trackboard_columns" />
            </Container>
        </>
    );
};
